import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-sm font-medium transition-colors duration-200 focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-brand-primary disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-brand-primary text-gray-100 shadow-sm hover:bg-brand-primaryAlpha focus-visible:outline-brand-secondary",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive-alpha",
        outline:
          "border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-gray-800 text-gray-100 shadow-sm hover:bg-gray-700 dark:bg-gray-300 dark:text-gray-1100 dark:hover:bg-gray-400",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        form: "bg-background text-gray-1100 hover:bg-backgroundAccent dark:text-gray-100",
        card: "border bg-card text-gray-1100 hover:bg-cardAccent dark:text-gray-100",
      },
      size: {
        default: "h-8 px-3 py-2 text-sm",
        sm: "h-6 rounded-sm px-3 text-xs",
        lg: "h-10 rounded-sm px-8",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
